import React, { useEffect, useState } from 'react'
import { TextField, Typography, Checkbox } from '@mui/material'
import { withStyles } from '@mui/styles'
import Autocomplete from '@mui/material/Autocomplete'

import { ListboxComponent } from './virtualized-list-box.component'
import { filterAutocompleteStyles } from './filter-autocomplete.styles'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'

const icon = <CheckBoxOutlineBlank fontSize="small" />
const checkedIcon = <CheckBox fontSize="small" />

/**
 * @typedef {object} FilterOption
 * @property {string | number} key - El identificador único de la opción.
 * @property {string} value - El texto legible que se mostrará para la opción.
 * @property {boolean} [special] - Si es `true`, la opción siempre se mostrará en los resultados del filtro, independientemente del texto de búsqueda.
 */

/**
 * @typedef {object} FilterChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {string | number | Array<string|number>} target.value - El valor seleccionado. Si `multiple` es `true`, será un array de claves; de lo contrario, será una única clave o una cadena vacía.
 */

/**
 * @typedef {object} FilterAutocompleteProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {string} [placeholder=''] - Texto de marcador de posición para el campo de entrada.
 * @property {function(FilterChangeEvent): void} onChange - La función callback que se ejecuta cuando la selección cambia.
 * @property {FilterOption[]} [values=[]] - El array de opciones disponibles para seleccionar.
 * @property {boolean} [multiple=false] - Si es `true`, permite la selección de múltiples valores con checkboxes.
 * @property {string | number} [value] - El valor actualmente seleccionado. Si `multiple` es `true`, debe ser una cadena de claves separadas por comas (ej. '1,2,3').
 * @property {string} [label] - La etiqueta que se muestra dentro del campo de entrada, antes del placeholder.
 */

/**
 * Un componente de autocompletado especializado para filtros, construido sobre `Autocomplete` de Material-UI.
 * 
 * Soporta selección simple o múltiple, virtualización de listas para un rendimiento óptimo con grandes
 * conjuntos de datos, y opciones especiales que siempre permanecen visibles en los resultados del filtro.
 * En modo múltiple, cada opción se renderiza con un checkbox para facilitar la selección.
 * 
 * El componente funciona como un componente controlado, gestionando su estado a través de las props `value` y `onChange`.
 * 
 * @component
 * @example
 * const MyFilterPanel = () => {
 *   const [selectedCategories, setSelectedCategories] = useState('1,3');
 * 
 *   const categories = [
 *     { key: 1, value: 'Electrónica' },
 *     { key: 2, value: 'Hogar' },
 *     { key: 3, value: 'Deportes' },
 *     { key: 4, value: 'Moda' },
 *     { key: 'all', value: 'Todas las categorías', special: true },
 *   ];
 * 
 *   const handleChange = (e) => {
 *     console.log(`Campo: ${e.target.name}, Valor: ${e.target.value}`);
 *     setSelectedCategories(e.target.value);
 *   };
 * 
 *   return (
 *     <FilterAutocomplete
 *       name="categoryFilter"
 *       label="Categoría"
 *       placeholder="Selecciona categorías..."
 *       values={categories}
 *       value={selectedCategories}
 *       onChange={handleChange}
 *       multiple
 *     />
 *   );
 * };
 * 
 * @param {FilterAutocompleteProps} props - Las props del componente FilterAutocomplete.
 */
export const FilterAutocomplete = withStyles(filterAutocompleteStyles)(({
  classes,
  name,
  placeholder = '',
  onChange,
  values = [],
  multiple = false,
  ...props
}) => {
  const [open, setOpen] = useState(false)

  // Determinar los valores seleccionados basados en la prop multiple
  const selectedValues = multiple
    ? props.value
      ? props.value.split(',').map((val) => values.find((item) => item.key.toString() === val))
      : []
    : props.value && values.length
      ? values.find((item) => item.key.toString() === props.value)
      : null

  const filterOptions = (options, state) => {
    const { inputValue, getOptionLabel } = state
    const value = inputValue.trim().toLowerCase()
    return options.filter((option) => {
      if (option.special) {
        return true
      }
      return getOptionLabel(option)?.toLowerCase().includes(value)
    })
    return options
  }

  const handleChange = (e, option) => {
    console.log('AS')
    onChange({
      target: {
        name,
        value: multiple ? (option ? option.map((opt) => opt.key) : []) : option ? option.key : '',
      },
    })
  }

  return (
    <Autocomplete
      id={`${name}-autocomplete-input`}
      size="small"
      multiple={multiple}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={values}
      getOptionLabel={(option) => option?.value}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      onChange={handleChange}
      value={selectedValues}
      autoSelect
      ListboxComponent={ListboxComponent}
      filterOptions={filterOptions}
      renderOption={(props, option, state) => (
        <li {...props}>
          {multiple ? (
            <React.Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={state.selected}
              />
              {option.value}
            </React.Fragment>
          ) : (
            option.value
          )}
        </li>
      )}
      renderInput={(params) => {
        params.InputProps.startAdornment = (
          <React.Fragment>
            <Typography variant="body1" className={classes.title}>
              {props.label}:
            </Typography>
            {params.InputProps.startAdornment}
          </React.Fragment>
        )
        return (
          <TextField
            {...params}
            className={classes.root}
            variant="outlined"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
            }}
          />
        )
      }}
    />
  )
})
